import React, { useEffect } from 'react';
import { UseFormReturn, FieldValues, Path } from 'react-hook-form';
import { RadioButton } from '@components/shared/radio-button/RadioButton';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { cn } from '@utils/index';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { getAvailableDependentBenefits } from '@features/onboarding/utils/criticalIllnessPriceId';
import { useAllowedPrices } from '@features/onboarding/hooks/useAllowedPrices';
import { calculateCoverageLevel } from '@features/onboarding/utils/coverageLevel';

interface CriticalIllnessCoverageSelectorProps<T extends FieldValues> {
  planId: string;
  plan: InsurancePlan;
  form: UseFormReturn<T>;
  name: string;
  viewMode: 'grid' | 'list' | 'tab';
  disabled?: boolean;
}

export const CriticalIllnessCoverageSelector = <T extends FieldValues>({
  planId,
  plan,
  form,
  viewMode,
  disabled,
}: CriticalIllnessCoverageSelectorProps<T>) => {
  const { setData, data } = useOnboardingStore();

  const sanitizeCategory = (category: string) =>
    category.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  const criticalIllnessPlanKey = `${sanitizeCategory(plan.category)}Plan`;
  const coverageAmountFieldName = criticalIllnessPlanKey.replace(
    'Plan',
    'PlanCoverageAmount'
  );
  const dependentBenefitFieldName = criticalIllnessPlanKey.replace(
    'Plan',
    'PlanDependentBenefit'
  );
  const priceFieldName = criticalIllnessPlanKey.replace('Plan', 'PlanPrice');

  const coverageOptions = [
    { value: 10000, label: viewMode === 'grid' ? '$10k' : '$10,000' },
    { value: 20000, label: viewMode === 'grid' ? '$20k' : '$20,000' },
    { value: 30000, label: viewMode === 'grid' ? '$30k' : '$30,000' },
    { value: 40000, label: viewMode === 'grid' ? '$40k' : '$40,000' },
  ];

  const dependentBenefitOptions = [
    { value: 5000, label: viewMode === 'grid' ? '$5k' : '$5,000' },
    { value: 10000, label: viewMode === 'grid' ? '$10k' : '$10,000' },
    { value: 15000, label: viewMode === 'grid' ? '$15k' : '$15,000' },
    { value: 20000, label: viewMode === 'grid' ? '$20k' : '$20,000' },
  ];

  const planTags = data.planTags || {};
  const planTagsForThisPlan = planTags[plan.uid] || [];
  const coverageLevel = calculateCoverageLevel(planTagsForThisPlan);

  const { allowedPrices, isFetched, isLoading } = useAllowedPrices(
    plan.id,
    coverageLevel
  );

  const getDependentInfo = () => {
    const dependentsCount = planTagsForThisPlan.filter((tag: string) =>
      tag.includes('dependent')
    ).length;
    const hasSpouse = planTagsForThisPlan.some((tag: string) =>
      tag.includes('spouse')
    );
    return { dependentsCount, hasSpouse };
  };

  const getCurrentValues = () => {
    const formCoverageAmount = form.watch(
      coverageAmountFieldName as Path<T>
    ) as number | undefined;
    const storeCoverageAmount = (data as Record<string, unknown>)[
      coverageAmountFieldName
    ] as number | undefined;
    const currentCoverageAmount =
      formCoverageAmount || storeCoverageAmount || 0;

    const formDependentBenefit = form.watch(
      dependentBenefitFieldName as Path<T>
    ) as number | undefined;
    const storeDependentBenefit = (data as Record<string, unknown>)[
      dependentBenefitFieldName
    ] as number | undefined;
    const currentDependentBenefit =
      formDependentBenefit || storeDependentBenefit || 5000;

    return { currentCoverageAmount, currentDependentBenefit };
  };

  const findPriceFromAllowed = (
    coverageAmount: number,
    dependentBenefit: number
  ): number => {
    if (allowedPrices.length === 0 || coverageAmount === 0) return 0;

    const { dependentsCount } = getDependentInfo();

    const match = allowedPrices.find((p) => {
      const d = p.data as
        | { mb: number; db: number; d: number }
        | undefined;
      if (!d) return false;
      if (d.mb !== coverageAmount) return false;
      if (dependentsCount > 0) {
        if (d.d !== 1) return false;
        if (dependentBenefit > 0 && d.db !== dependentBenefit)
          return false;
      } else {
        if (d.d !== 0) return false;
      }
      return true;
    });

    return match?.price || 0;
  };

  useEffect(() => {
    if (!isFetched || allowedPrices.length === 0) return;
    const { currentCoverageAmount, currentDependentBenefit } =
      getCurrentValues();
    if (currentCoverageAmount === 0) return;
    const newPrice = findPriceFromAllowed(
      currentCoverageAmount,
      currentDependentBenefit
    );
    if (newPrice > 0) {
      form.setValue(
        priceFieldName as Path<T>,
        newPrice as T[Path<T>]
      );
      setData({ [priceFieldName]: newPrice });
    }
  }, [isFetched, allowedPrices]);

  const handleCoverageChange = (optionValue: number) => {
    const { currentCoverageAmount, currentDependentBenefit } =
      getCurrentValues();

    // If clicking the already-selected option, clear selection
    if (currentCoverageAmount === optionValue) {
      form.setValue(
        coverageAmountFieldName as Path<T>,
        0 as T[Path<T>]
      );
      form.setValue(priceFieldName as Path<T>, 0 as T[Path<T>]);
      setData({
        [coverageAmountFieldName]: 0,
        [priceFieldName]: 0,
      });
      return;
    }

    const availableDepBenefits =
      getAvailableDependentBenefits(optionValue);
    let newDependentBenefit = currentDependentBenefit;
    if (!availableDepBenefits.includes(currentDependentBenefit)) {
      newDependentBenefit = 5000;
    }

    const newPrice = findPriceFromAllowed(
      optionValue,
      newDependentBenefit
    );

    form.setValue(
      coverageAmountFieldName as Path<T>,
      optionValue as T[Path<T>]
    );
    form.setValue(
      dependentBenefitFieldName as Path<T>,
      newDependentBenefit as T[Path<T>]
    );

    const updateData: Record<string, unknown> = {
      [coverageAmountFieldName]: optionValue,
      [dependentBenefitFieldName]: newDependentBenefit,
    };

    if (newPrice > 0 || !isLoading) {
      form.setValue(
        priceFieldName as Path<T>,
        newPrice as T[Path<T>]
      );
      updateData[priceFieldName] = newPrice;
    }

    setData(updateData);
  };

  const handleDependentBenefitChange = (optionValue: number) => {
    const { currentCoverageAmount } = getCurrentValues();
    const newPrice = findPriceFromAllowed(
      currentCoverageAmount,
      optionValue
    );

    form.setValue(
      dependentBenefitFieldName as Path<T>,
      optionValue as T[Path<T>]
    );

    const updateData: Record<string, unknown> = {
      [dependentBenefitFieldName]: optionValue,
    };

    if (newPrice > 0 || !isLoading) {
      form.setValue(
        priceFieldName as Path<T>,
        newPrice as T[Path<T>]
      );
      updateData[priceFieldName] = newPrice;
    }

    setData(updateData);
  };

  const { currentCoverageAmount, currentDependentBenefit } =
    getCurrentValues();
  const hasSelection = currentCoverageAmount > 0;
  const { dependentsCount } = getDependentInfo();
  const hasDependents = dependentsCount > 0;
  const availableDepBenefits = getAvailableDependentBenefits(
    currentCoverageAmount || 10000
  );

  return (
    <div
      className={cn(
        'flex flex-col gap-3 py-3 border-gray-200',
        viewMode !== 'tab' && 'border-t'
      )}
    >
      <p className="text-xs font-bold">
        Choose the amount of coverage you would like:
      </p>
      <fieldset className={cn('flex gap-4 items-center', disabled && 'pointer-events-none')} disabled={disabled}>
        {coverageOptions.map((option) => {
          const isSelectedCoverage =
            hasSelection && +currentCoverageAmount === option.value;
          return (
            <div
              key={option.value}
              className={cn(
                'font-normal transition-opacity',
                !hasSelection && 'opacity-40'
              )}
            >
              <RadioButton
                name={`${coverageAmountFieldName}_${planId}`}
                value={option.value.toString()}
                checked={isSelectedCoverage}
                onChange={() => handleCoverageChange(option.value)}
                label={option.label}
                disabled={disabled}
                labelProps={{
                  className: cn(
                    '!font-normal',
                    viewMode !== 'list' && '!ml-2'
                  ),
                }}
              />
            </div>
          );
        })}
      </fieldset>

      {hasSelection && hasDependents && (
        <>
          <p className="text-xs font-bold">
            Coverage amount for children can&apos;t be more than 50%
            of coverage chosen for member/spouse:
          </p>
          <fieldset className="flex gap-4">
            {dependentBenefitOptions
              .filter((option) =>
                availableDepBenefits.includes(option.value)
              )
              .map((option) => {
                const isSelectedBenefit =
                  +currentDependentBenefit === option.value;
                return (
                  <div key={option.value} className="font-normal">
                    <RadioButton
                      name={`${dependentBenefitFieldName}_${planId}`}
                      value={option.value.toString()}
                      checked={isSelectedBenefit}
                      onChange={() =>
                        handleDependentBenefitChange(option.value)
                      }
                      label={option.label}
                      labelProps={{
                        className: cn(
                          '!font-normal',
                          viewMode !== 'list' && '!ml-2'
                        ),
                      }}
                    />
                  </div>
                );
              })}
          </fieldset>
        </>
      )}
    </div>
  );
};
